import {
  NoRLHealthScore,
  SiteViewHealthScore,
} from 'features/siteView/aggregateSiteData'
import { useSiteViewColors } from 'features/siteView/utils/siteViewTheme'
import { isNil } from 'lodash'
import React, { FC, ReactElement, ReactNode } from 'react'
import styled from 'styled-components'
import { updateColorOpacity } from 'utils/colorHelpers'
import { toRoman } from 'utils/fde/conversionHelpers'

const HealthScoreDisplayText: FC<{
  text: string
  backgroundColor: string
  textContainer: ReactElement<{ children: ReactNode }>
  className?: string
}> = ({ text, backgroundColor, textContainer, className }) => {
  const textDisplay = React.cloneElement(textContainer, {
    children: (
      <>
        {textContainer.props.children}
        {text}
      </>
    ),
  })

  return (
    <HealthScoreDisplayTextContainer
      $backgroundColor={backgroundColor}
      className={className}>
      {textDisplay}
    </HealthScoreDisplayTextContainer>
  )
}

const HealthScoreDisplayTextContainer = styled.div<{
  $backgroundColor: string
}>`
  width: max-content;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
`

export interface HealthScorePillProps {
  healthScoreData:
    | {
        minRL: number | undefined
        percentBelowTMin: number | undefined
        healthScore: SiteViewHealthScore
        riskScore: number | undefined
      }
    | { isLoading: true }
  siteViewColors: ReturnType<typeof useSiteViewColors>
  textContainer: ReactElement<{ children: ReactNode }>
  className?: string
}
export const HealthScorePill: FC<HealthScorePillProps> = (props) => {
  const { siteViewColors, healthScoreData, textContainer } = props

  if ('isLoading' in healthScoreData) {
    return (
      <HealthScoreDisplayText
        text='Calculating...'
        backgroundColor='transparent'
        textContainer={textContainer}
        className={props.className}
      />
    )
  }

  const { minRL, percentBelowTMin, healthScore, riskScore } = healthScoreData

  if (healthScore === NoRLHealthScore.NoInspection) {
    return (
      <HealthScoreDisplayText
        text='No Inspection'
        backgroundColor={siteViewColors[healthScore]}
        textContainer={textContainer}
        className={props.className}
      />
    )
  }
  if (healthScore === NoRLHealthScore.NotEnoughData) {
    return (
      <HealthScoreDisplayText
        text='Inspected'
        backgroundColor={siteViewColors[healthScore]}
        textContainer={textContainer}
        className={props.className}
      />
    )
  }

  if (isNil(minRL) && isNil(riskScore)) {
    throw new Error(
      `minRLFromToday and riskScore is undefined, but health score is ${healthScore}`
    )
  }

  const text = !isNil(minRL)
    ? minRL <= 0
      ? `<0 yrs${isNil(percentBelowTMin) ? '' : ` (${percentBelowTMin.toFixed(2)}%<TMin)`}`
      : `${minRL.toFixed(2)} yrs`
    : !isNil(riskScore)
      ? `Class ${toRoman(riskScore)}`
      : undefined

  if (isNil(text)) {
    throw new Error(
      `Health score ${healthScore} has no text to display. minRL: ${minRL}, percentBelowTMin: ${percentBelowTMin}, riskScore: ${riskScore}`
    )
  }
  return (
    <HealthScoreDisplayText
      text={text}
      backgroundColor={updateColorOpacity(siteViewColors[healthScore], 0.5)}
      textContainer={textContainer}
      className={props.className}
    />
  )
}
